// MindMap → 正規化的 Markdown 大綱。parseOutline(serializeOutline(map)) 應得到相同的圖。
import { type MindMap, type Topic, walk } from './model.js';

// 屬性值含逗號、冒號或大括號時加引號。
function quote(value: string): string {
  return /[,:{}"]/.test(value) || value !== value.trim() ? `"${value.replace(/"/g, "'")}"` : value;
}

// 組出 `{...}` 屬性區塊；沒有屬性時回傳空字串。
function attrBlock(topic: Topic, forceId: Set<string>, ids: IdMode, extra: [string, string][] = []): string {
  const attrs: string[] = extra.map(([key, value]) => `${key}: ${quote(value)}`);
  if (ids === 'all' || (ids === 'explicit' && topic.explicitId) || forceId.has(topic.id)) attrs.push(`id: ${topic.id}`);
  if (topic.icon) attrs.push(`icon: ${topic.icon}`);
  if (topic.color) attrs.push(`color: ${topic.color}`);
  if (topic.boundary !== undefined) attrs.push(topic.boundary ? `boundary: ${quote(topic.boundary)}` : 'boundary');
  if (topic.collapsed) attrs.push('collapsed');
  return attrs.length ? ` {${attrs.join(', ')}}` : '';
}

// 標題本身若以 {…} 結尾，補空屬性區塊避免被誤判。
function safeTitle(title: string): string {
  return /\{[^{}]*\}\s*$/.test(title) ? `${title} {}` : title;
}

// 備註逐行輸出為 `> ` 區塊。
function noteLines(topic: Topic, indent: string): string[] {
  return topic.note ? topic.note.split('\n').map(line => `${indent}> ${line}`.trimEnd()) : [];
}

/** id 輸出方式：explicit = 只輸出作者指定的；all = 全部（供工具以 id 操作）；none = 只輸出關聯線必須的。 */
export type IdMode = 'explicit' | 'all' | 'none';

// 主要入口。
export function serializeOutline(map: MindMap, options: { ids?: IdMode } = {}): string {
  const ids = options.ids ?? 'explicit';
  // 關聯線端點：標題唯一時用標題參照，否則輸出 id。
  const titleCount = new Map<string, number>();
  const titles = new Map<string, Topic>();
  walk(map.root, topic => { titleCount.set(topic.title, (titleCount.get(topic.title) ?? 0) + 1); titles.set(topic.id, topic); });
  const forceId = new Set<string>();
  const reference = (id: string): string => {
    const topic = titles.get(id)!;
    if ((ids !== 'none' && topic.explicitId) || ids === 'all' || titleCount.get(topic.title)! > 1 || /[\[\]]/.test(topic.title) || topic.title.startsWith('#')) { forceId.add(id); return `#${id}`; }
    return topic.title;
  };
  const relationLines = map.relations.map(relation => `[${reference(relation.from)}] -> [${reference(relation.to)}]${relation.label ? `: ${relation.label}` : ''}`);
  const rootExtra: [string, string][] = [];
  if (map.structure !== 'balanced') rootExtra.push(['structure', map.structure]);
  if (map.theme !== 'sketch') rootExtra.push(['theme', map.theme]);
  const lines = [`# ${safeTitle(map.root.title)}${attrBlock(map.root, forceId, ids, rootExtra)}`, ...noteLines(map.root, '')];
  const visit = (topic: Topic, depth: number) => {
    const indent = '  '.repeat(depth - 1);
    lines.push(`${indent}- ${safeTitle(topic.title)}${attrBlock(topic, forceId, ids)}`, ...noteLines(topic, indent + '  '));
    topic.children.forEach(child => visit(child, depth + 1));
  };
  map.root.children.forEach(child => visit(child, 1));
  if (relationLines.length) lines.push('', ...relationLines);
  return lines.join('\n') + '\n';
}
